import React, { Component } from "react";
import PropTypes from "prop-types";

import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { IoStarOutline, IoStar } from "react-icons/io5";
import isEmpty from "lodash/isEmpty";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";

import * as projectActions from "../../actions/projectActions";
import * as customerActions from "../../actions/customerActions";
import * as contractActions from "../../actions/contractActions";
import * as callActions from "../../actions/callActions";
import * as departmentActions from "../../actions/departmentActions";
import * as userActions from "../../actions/userActions";
import * as modalActions from "../../actions/modalActions";
import * as codeActions from "../../actions/codeActions";
import ReportService from "../../services/reportService";
import FileService from "../../services/fileService";

import HeaderWrapper from "../common/HeaderWrapper";
import ProjectForm from "./ProjectForm";
import ProjectView from "./ProjectView";
import ProjectNotesPage from "./notes/ProjectNotesPage";
import ProjectNotificationsPage from "./notifications/ProjectNotificationsPage";
import ProjectExpensePage from "./ProjectExpensePage";
import ProjectMetrics from "./ProjectMetrics";
import ProjectProgressBar from "./ProjectProgressBar";
import HoursByUser from "./charts/HoursByUser";
import Loading from "../common/Loading";
import Alert from "../common/Alert";
import ConfirmModal from "../common/ConfirmModal";
import { customToast as toast } from "../../utils";

import ProjectProgressReviewSlider from "./ProjectProgressReviewPage/ProjectProgressReviewSlider";
import MonthlySegmentProgressBar from "./MonthlySegmentProgressBar";
import MonthlySegmentDisplay from "./MonthlySegmentDisplay";

class ProjectEditPage extends Component {
  constructor(props) {
    super(props);
    this.scrollToTopRef = React.createRef();
    this.state = { editing: false, isFavorite: false, data: null, pm: {} };
  }

  componentDidMount = async () => {
    const { users, customers, departments, match, actions, codes } = this.props;

    this.scrollToTopRef.current.scrollIntoView({ behavior: "auto" });

    if (!users.length) actions.loadUsers();
    if (!customers.length) actions.loadCustomers();
    if (!departments.length) actions.loadDepartments();
    if (!codes.length) actions.loadCodes();
    // getProject calls findById on api not getProject
    actions.getProject(match.params.id);

    const idx = this.props.auth.user.favorites.indexOf(match.params.id);

    const isFavorite = idx >= 0;
    this.setState({ isFavorite });
  };

  componentWillUnmount = () => {
    this.props.actions.clearProject();
  };

  onClickFavorite = () => {
    this.setState({ isFavorite: !this.state.isFavorite });
    this.props.actions.updateFavorites(
      this.props.auth.user,
      this.props.project._id
    );
  };

  getProjectManagementData = async () => {
    const result = await ReportService.get(
      `/projects/managed-service/${this.props.match.params.id}`
    );

    if (result) {
      const { data, ...pm } = result;
      this.setState({ data, pm });
    }
  };

  handleEdit = () => {
    this.setState({ editing: !this.state.editing });
  };

  handleSave = async (project) => {
    project.id = this.props.project._id;
    const result = await this.props.actions.updateProject(project, false);
    if (result) {
      this.setState({ editing: false });
    }
  };

  handleViewDetails = () => {
    this.props.actions.showModalSuccess("projectDetailsModal");
  };

  handleCancel = () => {
    this.setState({ editing: false });
  };

  UNSAFE_componentWillReceiveProps = (nextProps) => {
    if (this.props.project !== nextProps.project) {
      if (nextProps.project.customer) {
        this.props.actions.loadContractsByCustomerId(
          nextProps.project.customer._id
        );
      }

      if (this.props.auth.user.favorites) {
        const idx = this.props.auth.user.favorites.indexOf(
          nextProps.project.id
        );
        const isFavorite = idx >= 0;
        this.setState({ isFavorite });
      }

      if (nextProps.project.start_date && nextProps.project.end_date) {
        this.getProjectManagementData();
      }
    }
  };

  handleUpdate = () => {
    const { actions, match } = this.props;
    // getProject calls findById on api
    actions.getProject(match.params.id);
  };

  handleDuplicate = () => {
    this.props.actions.showModalSuccess("cloneProjectConfirmModal");
  };

  onCloneProjectConfirm = async () => {
    await this.props.actions.cloneProject(this.props.project.id, true);
    this.props.actions.getProject(this.props.match.params.id);
  };

  uploadFile = async (files) => {
    const invalidFileNames = files.find((file) => file.name.length > 255);
    if (invalidFileNames) {
      toast.error("The name of the file is too long");
    } else {
      await this.props.actions.uploadFile(this.props.project, files, true);
      await this.props.actions.getProject(this.props.project.id);
    }
  };

  onClickDownload = async (file) => {
    const token = localStorage.getItem("grav_token");
    const imageSource = await FileService.getFileWithToken(
      `Projects/${file}`,
      token
    );
    window.open(imageSource);
  };

  render() {
    const {
      project,
      customers,
      users,
      contactsByCustomer,
      departments,
      codes,
      auth,
      isMobile
    } = this.props;
    const { isFavorite, editing, data, pm } = this.state;

    const projectCategory = codes.find(
      (code) =>
        code.entity === "Project" &&
        code.field === "project_category" &&
        code.label === "Project"
    );

    const showPercentSlider =
      projectCategory && projectCategory._id
        ? project.category === projectCategory._id
        : false;

    const currentMonthlySegment = project.budget_segments?.find(
      (segment) =>
        new Date() >= startOfDay(new Date(segment.startDate)) &&
        new Date() <= endOfDay(new Date(segment.endDate))
    );

    return (
      <React.Fragment>
        <div
          style={{ float: "left", clear: "both" }}
          ref={this.scrollToTopRef}
        />
        <HeaderWrapper
          breadcrumbs={[{ label: project.name }]}
          onClickEdit={auth.maxRole > 1000 && this.handleEdit}
          onDuplicate={auth.maxRole > 1000 && this.handleDuplicate}
          isMobile={isMobile}
          icon={
            isFavorite ? (
              <IoStar
                className="favorite"
                style={{ color: "#FFD275" }}
                onClick={this.onClickFavorite}
              />
            ) : (
              <IoStarOutline
                className="favorite"
                style={{ color: "#848487" }}
                onClick={this.onClickFavorite}
              />
            )
          }
        />
        <div style={{ margin: "15px 15px 0 15px" }}>
          <Alert show={project?.customer?.bc_blocked === "All"}>
            <p>This customer’s account has been blocked in Business Central</p>
          </Alert>
        </div>
        {isEmpty(project) ? (
          <div className="content-wrapper vertical-center -full">
            <Loading />
          </div>
        ) : (
          <React.Fragment>
            {editing ? (
              <ProjectForm
                project={project}
                customers={customers}
                codes={codes}
                departments={departments}
                contracts={contactsByCustomer}
                users={users}
                editing={editing}
                saving={this.props.savingProject}
                onSave={this.handleSave}
                onCancel={this.handleCancel}
              />
            ) : (
              <React.Fragment>
                <div className="content-wrapper">
                  <ProjectProgressBar
                    project={project}
                    showDetails
                    auth={auth}
                  />
                  {currentMonthlySegment && (
                    <MonthlySegmentProgressBar
                      project={project}
                      height={5}
                      showDetails
                    />
                  )}
                </div>
                {data && Object.keys(data).length ? (
                  <HoursByUser
                    auth={auth}
                    project={project}
                    data={data}
                    pm={pm}
                  />
                ) : null}
                <ProjectMetrics project={project} pm={pm} />
                {showPercentSlider && (
                  <div className="content-wrapper">
                    <ProjectProgressReviewSlider
                      value={
                        project.percent_complete.length
                          ? project.percent_complete[
                              project.percent_complete.length - 1
                            ].value
                          : 0
                      }
                      projectId={project._id}
                      disabled={auth.maxRole < 5000}
                      fullView
                      onProgressUpdate={this.handleUpdate}
                    />
                  </div>
                )}
                <ProjectView
                  project={project}
                  auth={auth}
                  uploadFile={this.uploadFile}
                  downloadFile={this.onClickDownload}
                  loading={this.props.savingProject}
                />
              </React.Fragment>
            )}
            <MonthlySegmentDisplay
              data={project.budget_segments}
              project={project}
            />
            <ProjectNotificationsPage
              project={project}
              codes={codes}
              isMobile={isMobile}
            />
            <ProjectExpensePage project={project} isMobile={isMobile} />
            <ProjectNotesPage isMobile={isMobile} />
          </React.Fragment>
        )}
        <ConfirmModal
          id="cloneProjectConfirmModal"
          title="Clone Project"
          body="Are you sure you want to clone this project?"
          close={this.props.actions.hideModal}
          confirm={this.onCloneProjectConfirm}
        />
      </React.Fragment>
    );
  }
}

ProjectEditPage.propTypes = {
  actions: PropTypes.object.isRequired,
  savingProject: PropTypes.bool,
  codes: PropTypes.array,
  customers: PropTypes.array,
  users: PropTypes.array,
  departments: PropTypes.array,
  match: PropTypes.object,
  auth: PropTypes.object,
  project: PropTypes.object,
  contactsByCustomer: PropTypes.array,
  isMobile: PropTypes.bool
};

function mapStatesToProps(state) {
  return {
    state: state.reducers,
    auth: state.reducers.auth,
    user: state.reducers.user,
    project: state.reducers.project,
    savingProject: state.reducers.savingProject,
    customers: state.reducers.customers,
    departments: state.reducers.departments,
    users: state.reducers.users,
    contactsByCustomer: state.reducers.contactsByCustomer,
    codes: state.reducers.codes
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...projectActions,
        ...customerActions,
        ...contractActions,
        ...callActions,
        ...departmentActions,
        ...userActions,
        ...modalActions,
        ...codeActions
      },
      dispatch
    )
  };
}

export default connect(mapStatesToProps, mapDispatchToProps)(ProjectEditPage);
